import { Button, For, Icon, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@components/ui/tooltip';
import { FunctionComponent, JSX, memo, ReactNode, SVGProps, useState } from 'react';
import { FormattedMessage, PrimitiveType } from 'react-intl';

type Action<T extends string> = {
  i18nKey: string;
  action: T;
  values?: Record<string, PrimitiveType>;
  disabled?: boolean;
};

type Props<T extends string> = {
  description: string;
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  subDescription?: string;
  tooltip?: string;
  disabledTooltip?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  actions?: readonly Action<T>[];
  children?: ReactNode;
  onClick?: (action: T) => void;
};

function DataSheetCard<T extends string>({
  description,
  icon,
  subDescription,
  actions,
  children,
  tooltip,
  isDisabled = false,
  isLoading = false,
  disabledTooltip = false,
  onClick,
}: Readonly<Props<T>>) {
  const [selectedAction, setSelectedAction] = useState<T | null>(null);

  const handleClick = (action: T) => {
    if (selectedAction === action) {
      setSelectedAction(null);
    } else {
      setSelectedAction(action);
    }
  };

  return (
    <VStack
      width="100%"
      height="100%"
      backgroundColor="neutral.700"
      cursor="default"
      padding="standardM"
      gap="standardS"
    >
      {icon && <Icon as={icon} boxSize={{ base: '32px', '4k': '40px' }} color="neutral.300" />}
      <Text whiteSpace="pre-line" textAlign="center">
        {description}
      </Text>
      {subDescription && (
        <Text whiteSpace="pre-line" textAlign="center" color="neutral.300">
          {subDescription}
        </Text>
      )}
      {actions && (
        <VStack gap={0} width="100%" marginTop="auto">
          <For each={actions}>
            {(action, index) => {
              const isSelected = selectedAction === action.action;
              return (
                <Button
                  key={action.action}
                  variant="toggle"
                  size="toggleM"
                  marginTop={`${index * -2}px`}
                  data-active={isSelected}
                  zIndex={isSelected ? 1 : 0}
                  disabled={isDisabled || isLoading || action.disabled}
                  _hover={{ zIndex: 2 }}
                  onClick={() => handleClick(action.action)}
                >
                  <FormattedMessage id={action.i18nKey} values={action.values} />
                </Button>
              );
            }}
          </For>
        </VStack>
      )}
      {children}
      {onClick && (
        <Tooltip content={tooltip} disabled={!tooltip || disabledTooltip}>
          <Button
            disabled={isDisabled || selectedAction === null}
            loading={isLoading}
            onClick={() => selectedAction !== null && onClick(selectedAction)}
          >
            <FormattedMessage id="global.validate" />
          </Button>
        </Tooltip>
      )}
    </VStack>
  );
}

export default memo(DataSheetCard) as <T extends string>(props: Props<T>) => JSX.Element;
